/**
 * HomeRequests — the home-wide Requests tab a `reception` or `admin` account
 * opens (T2075 M4; plan R-5, R-14, A8, D8, D13). Every request of the home the
 * R-9 flag lets this account see, as `CardRequest` rows on the R4 shape (DESIGN.md
 * §11: only a SHAPE change fits a wide list at 1024), Upcoming by default and
 * Past by chip, filterable by group, staff member and status, with a By-staff
 * view that groups the rows under a staff heading (most requests first).
 *
 * Declared interactive count: 6 + groups (Show x2, View x2, the staff select,
 * the status select, and one filter chip per group), plus, since board 2732 M3,
 * My requests' per-row Cancel and Add the return time on Upcoming rows
 * (`request-actions.tsx`), and since board 2788 M5 its change, its two asks and
 * the closed History on those rows. The <=7 rule is the kiosk's; the portal has no
 * control-budget spec (an R-18 finding, recorded in the plan's ledger rather
 * than here).
 *
 * ⛔ THE SERVER FILTERS `group` AND `staff` ONLY. Upcoming / Past is bucketed
 * HERE by `dublinDayNumber`, exactly as `MyRequests.tsx` does, and under the
 * same Captain ruling: a row with NO readable day (a repeating pattern row, or
 * a malformed date) is SHOWN under Upcoming, never silently hidden; and if
 * today's own day cannot be read the bucket fails OPEN and shows everything.
 * Status is filtered here too, on the word the nurse vocabulary gives the row's
 * STATUS (`myRequestsCopy.nurseStatusLabels`: `new` and `priced` both map to
 * `Sent`), so a select that offers `Sent` matches both, which a server
 * `status = $x` could not. ⚠️ Since board 2707 M4 that is NOT always the word
 * the row's pill shows: a seen `new` or `priced` row's pill reads `Seen`
 * (ruling RG) and `Sent` still keeps it, as "not yet confirmed". Whether the
 * select should offer `Seen` is routed to the plan's M7 OPEN-QUESTIONS rows.
 * Both run over the wire's capped list (`HOME_REQUESTS_CAP`, stated in the
 * screen's hint so an older past row is explained rather than hidden).
 *
 * ⭐ board 2707 (M4, R-7) — A POLL BEHIND `live`, DEFAULT OFF. The list loads
 * on mount and on every filter change; with `live` it also refreshes every
 * `LIST_REFRESH_MS` while the tab is visible, at once on return to the tab, and
 * at once when `live` flips back on, halting on 401 / 403 / 429 and never
 * surfacing a background failure: `MyRequests`' own tick, to the letter, so the
 * idle-triple bound (plan D14) holds here as it does there. ⭐ board 2725 / 2732
 * R-11: the shell passes `live={!idle}` (`app.tsx`), so the list refreshes on its
 * own while attended and stops once the idle nudge shows; the default OFF is
 * what any other mount (a spec) gets.
 *
 * ⭐ THE SCREEN COMPOSES `@ecmb/ui` AND DRAWS NOTHING: `CardRequest` rows,
 * `Chip`'s filter shape for the three chip groups and its neutral status shape
 * for the group on a row, `FormField kind="select"` for the two selects,
 * `EmptyState` (neutral, never the ok tint) for the gated-OFF and filtered-empty
 * states, `Button` for Try again. The class-level debt the census reads off
 * this composition (`facts-table` and `tone` per row, `field-label` and
 * `login-input` per select) is declared as measured in
 * `test/client/lib/adoption-debt.ts`.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { ApiClient } from '../api.js';
import { homeRequestsCopy, myRequestsCopy, myResidentsCopy, sharedCopy } from '../copy.js';
import { dublinDayNumber, formatReturnAt } from '../format.js';
import type {
  AskKind,
  AskState,
  DeclineReason,
  RequestStatus,
  ReturnAtState,
  SchedulePattern,
  TimeMeaning,
} from '../../shared/request-schema.js';
import type { PortalRole } from '../../shared/roles.js';
import { HOME_REQUESTS_CAP } from '../../core/home-request-projection.js';
import { LIST_REFRESH_MS, seenFor } from './MyRequests.js';
import { useRequestActions, type RequestHistoryView } from '../request-actions.js';
import { Button, CardRequest, Chip, EmptyState, FormField, Notice, formatWhenParts, type CardRequestWhen } from '@ecmb/ui';

/** The wire shape of `GET /api/home/requests` (`home-request-projection.ts`'s
 *  keep-set), declared here as the RENDER contract rather than imported: the
 *  projection is the server's pin, this is the screen's. */
export type HomeRequestListItem = {
  requestId: number;
  jobRef: string | null;
  residentDisplayName: string;
  roomAtBooking: string;
  destinationName: string;
  returnTrip: boolean;
  returnAt: string | null;
  /** ⭐ T2088 M4 (ADR-0006, plan D3) — which null `returnAt` is; `'to-confirm'`
   *  reads "time to confirm" in the instant's slot. On the wire since M2
   *  (`home-request-projection.ts`'s keep-set); the `MyRequests` type says more. */
  returnAtState: ReturnAtState;
  journeyDate: string | null;
  /** ⭐ board 2188 — the derived collection. This is the SCREEN's mirror of
   *  `core/home-request-projection.ts`'s keep-set; the two must move together,
   *  and the projection's spec is what grades the wire. Null on every pre-v12
   *  row and every `collection` run. */
  pickupAt: string | null;
  schedulePattern: SchedulePattern | null;
  timeMeaning: TimeMeaning | null;
  status: RequestStatus;
  requestedBy: string | null;
  groupLabelAtBooking: string | null;
  enteredByRole: PortalRole;
  /** ⭐ board 2707 (M1, R-2) — when the request was made, an ISO instant, or
   *  null; the card's `Requested` row. */
  createdAt: string | null;
  /** ⭐ board 2732 (M1, R-10) — whether East Coast Minibus has seen it, never
   *  when; `seenFor` turns it into the card's Seen node and pill word, exactly as
   *  on My requests. Optional because an older payload carries no key. */
  seen?: boolean;
  /** ⛔ board 2732 M2 (ADR-0008) — the request's notes, PRESENT only when the
   *  home's cross-group switch is ON (the server decides), ABSENT otherwise; the
   *  card's slot renders nothing when it is absent. */
  notes?: string;
  /** ⭐ Board 2788 M5 - the change view (`RequestChangeView`, on this wire since
   *  M3), rendered by `request-actions.tsx` exactly as on My requests. Optional
   *  for `seen`'s reason. */
  homeChanged?: boolean;
  askKind?: AskKind | null;
  askState?: AskState;
  askJourneyDate?: string | null;
  askPickupAt?: string | null;
  askDeclineReason?: DeclineReason | null;
  history?: readonly RequestHistoryView[];
};

type HomeRequestsResponse = { requests: HomeRequestListItem[]; crossGroupEnabled: boolean };
type MetaStaff = { staffId: number; displayName: string; groupLabel: string };
type MetaResponse = { staff?: MetaStaff[]; groups?: string[] };

type Show = 'upcoming' | 'past';
type View = 'date' | 'staff';

/**
 * The distinct nurse words, in map order (`Sent` once, though two statuses
 * read it). Exported for the spec; pure.
 */
export function statusWords(): string[] {
  return [...new Set(Object.values(myRequestsCopy.nurseStatusLabels))];
}

/**
 * Upcoming / Past, by Dublin day — `MyRequests.tsx`'s bucketing, kept to the
 * letter (see the header). Exported for the spec; pure.
 */
export function inShowBucket(journeyDate: string | null, show: Show, nowIso: string): boolean {
  const todayNo = dublinDayNumber(nowIso);
  const d = dublinDayNumber(journeyDate);
  if (d === null || todayNo === null) return show === 'upcoming';
  return show === 'upcoming' ? d >= todayNo : d < todayNo;
}

/** The card's facts from the row: `MyRequests.tsx`'s `whenFor`, on this
 *  screen's row type. A time that cannot be read is SAID, never left blank. */
function whenFor(item: HomeRequestListItem): CardRequestWhen {
  const parts = formatWhenParts({
    journeyDate: item.journeyDate,
    schedulePattern: item.schedulePattern,
    timeMeaning: item.timeMeaning,
  });
  const when: CardRequestWhen = { day: parts.date };
  if (parts.clock === null) when.time = myRequestsCopy.timeNotStated;
  else if (item.timeMeaning === 'collection') when.pickup = parts.clock;
  else if (item.timeMeaning === 'appointment') when.appointment = parts.clock;
  else when.time = parts.clock;
  // ⭐⭐ THE DERIVED COLLECTION (board 2188, Alex 2026-09-18). Reception meets the
  // bus at the door, so this is the fact their job turns on.
  // ⛔ ON THE `appointment` BRANCH ONLY, AND THE SLOT IS FREE THERE BY THE RULE
  // ABOVE — the branches are exclusive, so a `collection` row has already taken
  // `when.pickup` for the nurse's own stated time and never carries a `pickupAt`
  // to fight over (the validator refuses the pair by name). No new word, no new
  // slot, no `Card.tsx` change: `Pickup` beside `Appointment` is exactly what
  // R-2073-c removed, so restoring it IS the reversal.
  // ⚠️ Null on every pre-v12 row and every `collection` run, hence the guard.
  // ⛔ A LITERAL, NEVER THE ROW — the same rule the `formatWhenParts` call above
  // carries: `WhenInput` also accepts `timeZone` and `locale`, so handing it the
  // wire object would let a row override the pinned Europe/Dublin / en-IE pair.
  // Reusing it is also what keeps both clocks on one row in the SAME zone.
  if (item.timeMeaning === 'appointment' && item.pickupAt !== null) {
    // ⛔⛔ THE DAY RIDES WITH IT WHEN IT DIFFERS (security review M1, board 2188).
    // The card has ONE day slot and it belongs to the APPOINTMENT, so a bare
    // clock here silently borrows the wrong date. That is reachable on
    // LEGITIMATE data, with no crafted body: the database allows a pickup offset
    // up to 480 minutes, so a 07:30 appointment derives 23:30 the night before,
    // and the card would read `Tue 20 Oct · Pickup 23:30` for a bus that called
    // on the Monday. Measured, not theorised.
    // ⚠️ The day is appended to the VALUE rather than given its own slot: the
    // shared `CardRequestWhen` has four fixed slots and adding a fifth is a
    // design-system change this row may not make.
    const pickupParts = formatWhenParts({ journeyDate: item.pickupAt });
    if (pickupParts.clock !== null) {
      const sameDay = dublinDayNumber(item.pickupAt) === dublinDayNumber(item.journeyDate);
      when.pickup = sameDay ? pickupParts.clock : `${pickupParts.clock} (${pickupParts.date})`;
    }
  }
  return when;
}

/** The list path with the two server filters, or the bare path when neither is
 *  set. Built by hand with `encodeURIComponent`: SC-C1 (`my-requests-filter.spec.tsx`)
 *  pins the portal client free of `location.search` / `location.hash` /
 *  `URLSearchParams` so no filter can ever become URL state, and this is a
 *  fetch path, not the page's URL. Exported for the spec; pure. */
export function listPath(group: string | null, staffId: string): string {
  const parts: string[] = [];
  if (group !== null) parts.push(`group=${encodeURIComponent(group)}`);
  if (staffId !== '') parts.push(`staff=${encodeURIComponent(staffId)}`);
  return parts.length === 0 ? '/api/home/requests' : `/api/home/requests?${parts.join('&')}`;
}

export type HomeRequestsProps = {
  api: ApiClient;
  /** ⭐ board 2707 (M4, R-7) — IS RECEPTION ATTENDING THE TAB? Default
   *  `false`, so a mount that does not pass it polls nothing (see the header).
   *  `true` arms `MyRequests`' background tick; the flip back to `true` is one
   *  refetch. The shell passes `!idle` since board 2732 (R-11). */
  live?: boolean;
};

export function HomeRequests({ api, live = false }: HomeRequestsProps) {
  const [items, setItems] = useState<HomeRequestListItem[] | null>(null);
  const [crossGroupEnabled, setCrossGroupEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staff, setStaff] = useState<MetaStaff[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [show, setShow] = useState<Show>('upcoming');
  const [group, setGroup] = useState<string | null>(null);
  const [view, setView] = useState<View>('date');
  const [staffId, setStaffId] = useState('');
  const [status, setStatus] = useState('');
  // Try again bumps this; the list effect below is keyed on it.
  const [retry, setRetry] = useState(0);
  // A response from an EARLIER request that lands after a later one is dropped
  // rather than overwriting the newer list (`MyRequests.tsx`'s `latestLoad`).
  const latestLoad = useRef(0);
  // ⭐ board 2707 (M4) — `MyRequests`' two poll refs: `halted` is set by a 401 /
  // 403 / 429 tick and cleared by every resume path (the return to the tab, the
  // idle→active flip, a load); `wasLive` tells that flip from a filter re-run,
  // so the mount and a filter change each issue exactly ONE list GET.
  const haltedRef = useRef(false);
  const wasLive = useRef(live);

  const load = async (): Promise<void> => {
    const seq = latestLoad.current + 1;
    latestLoad.current = seq;
    haltedRef.current = false; // a load is a resume path (MyRequests' rule)
    setError(null);
    const res = await api.get<HomeRequestsResponse>(listPath(group, staffId));
    if (seq !== latestLoad.current) return;
    if (res.kind === 'network-error') {
      setError(sharedCopy.networkError);
      return;
    }
    if (res.status !== 200) {
      setError(homeRequestsCopy.loadError);
      return;
    }
    setItems(res.body.requests);
    setCrossGroupEnabled(res.body.crossGroupEnabled === true);
  };

  // The picker data (the home's staff and group labels) once, from the same
  // read the request form uses; the list on mount and on every server filter.
  useEffect(() => {
    void (async () => {
      const res = await api.get<MetaResponse>('/api/meta');
      if (res.kind !== 'response' || res.status !== 200) return;
      setStaff(res.body.staff ?? []);
      setGroups(res.body.groups ?? []);
    })();
  }, [api]);
  useEffect(() => {
    void load();
  }, [group, staffId, retry]);

  /** ⭐ board 2707 (M4, R-7 ready) — THE BACKGROUND TICK, `MyRequests`' own:
   *  the same GET (with the filters in force) and the same `latestLoad` guard,
   *  none of the first-paint instruments. It never RAISES an error; on 200 it
   *  replaces the list and clears a shown load error; on 401 / 403 / 429 it
   *  halts (a 401 has already relocked the shell inside `api.get`); anything
   *  else, or a throw, leaves the shown list as it was.
   *  ⛔ IT BUMPS `latestLoad` ONLY WHEN IT APPLIES AN ANSWER (`MyRequests`'
   *  `refresh` says why, and carries the one `simplified:` ordering left): a
   *  tick that starts while the first `load()` is in flight cannot make that
   *  load drop its answer and strand the screen on Loading, and an older load
   *  that lands after a tick applied cannot overwrite the newer list. This spec
   *  pins both (a tick answered 500 or 401 while the first GET is pending; a
   *  tick answered first, then the older first GET). */
  const refresh = async (): Promise<void> => {
    const seq = latestLoad.current;
    let res;
    try {
      res = await api.get<HomeRequestsResponse>(listPath(group, staffId));
    } catch {
      return;
    }
    if (seq !== latestLoad.current) return;
    if (res.kind === 'network-error') return;
    if (res.status === 401 || res.status === 403 || res.status === 429) {
      haltedRef.current = true;
      return;
    }
    if (res.status !== 200) return;
    latestLoad.current += 1; // applied: any older in-flight load or tick is now stale
    setItems(res.body.requests);
    setCrossGroupEnabled(res.body.crossGroupEnabled === true);
    setError(null);
  };

  // While `live`: every LIST_REFRESH_MS and at once on `visibilitychange` →
  // visible, and nothing while the tab is hidden. Re-armed on a filter change so
  // the tick reads the filters in force; only the idle→active flip itself
  // refetches at once (the load effect above already owns mount and filters).
  useEffect(() => {
    const tick = (): void => {
      if (haltedRef.current || document.visibilityState !== 'visible') return;
      void refresh();
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        haltedRef.current = false;
        tick();
      }
    };
    if (live && !wasLive.current) {
      haltedRef.current = false;
      tick();
    }
    wasLive.current = live;
    if (!live) return undefined;
    const id = setInterval(tick, LIST_REFRESH_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, group, staffId]);

  // ⭐ board 2732 M3 (R-3, DD8) — My requests' Cancel and Add the return time,
  // the SAME code (`request-actions.tsx`) over the same two routes; this screen
  // hands in its own list read and its own network-error state.
  const { actionsFor } = useRequestActions({
    api,
    reload: load,
    onNetworkError: () => setError(sharedCopy.networkError),
  });

  const chooseGroup = (g: string | null): void => {
    const next = g === null || group === g ? null : g;
    setGroup(next);
    // A group narrows the staff select; a name outside it is no longer a pick.
    const chosen = staff.find((s) => String(s.staffId) === staffId);
    if (next !== null && chosen !== undefined && chosen.groupLabel.toLowerCase() !== next.toLowerCase()) setStaffId('');
  };

  const offeredStaff = group === null ? staff : staff.filter((s) => s.groupLabel.toLowerCase() === group.toLowerCase());
  const staffOptions = [
    { value: '', label: homeRequestsCopy.staffAny },
    ...offeredStaff.map((s) => ({ value: String(s.staffId), label: s.displayName })),
  ];
  const statusOptions = [
    { value: '', label: homeRequestsCopy.statusAny },
    ...statusWords().map((w) => ({ value: w, label: w })),
  ];

  // ⭐ ONE "now" per render, read by the bucket filter below AND by each row's
  // actions, so a row can never straddle midnight against its own bucket.
  const nowIso = new Date().toISOString();

  function row(item: HomeRequestListItem) {
    // ⭐ board 2707 (M4, DD5) — `MyRequests`' derivation, so both lists say Seen
    // alike; no `Seen` time row since board 2732.
    const seenFacts = seenFor(item.status, item.seen);
    return (
      <li key={item.requestId} class="my-requests-row">
        <CardRequest
          layout="row"
          subject={homeRequestsCopy.subject(item.residentDisplayName, myResidentsRoom(item.roomAtBooking))}
          reference={item.jobRef !== null ? homeRequestsCopy.jobRefLabel(item.jobRef) : undefined}
          destination={item.destinationName}
          // ⭐ T2088 M4 (ADR-0006, plan D3) — THE SAME FOUR ARMS AS `MyRequests`,
          // and the same words: `myRequestsCopy.returnToConfirm` on purpose, so
          // the nurse's list and the home's list cannot phrase one state two
          // ways. A timeless `'unset'` return keeps the bare phrase.
          returnSuffix={
            item.returnTrip
              ? ` ${homeRequestsCopy.returnTripLabel} ${
                  item.returnAtState === 'to-confirm' ? myRequestsCopy.returnToConfirm : formatReturnAt(item.returnAt)
                }`.trimEnd()
              : undefined
          }
          status={item.status}
          seen={seenFacts.seen}
          waitingOn={seenFacts.waitingOn}
          when={whenFor(item)}
          stepLabel={myRequestsCopy.lifecycleLabel}
          // The STAFF name and the booking-time group as a neutral chip; a row
          // that names nobody carries the group alone, and a pre-v13 row neither
          // (the slot rule: no label over nothing).
          requesterSlot={
            item.requestedBy !== null || item.groupLabelAtBooking !== null ? (
              <>
                {item.requestedBy}
                {item.groupLabelAtBooking !== null && (
                  <>
                    {item.requestedBy !== null ? ' ' : ''}
                    <Chip neutral>{item.groupLabelAtBooking}</Chip>
                  </>
                )}
              </>
            ) : undefined
          }
          // ⭐ board 2707 (M1, R-2) — when it was asked for, `MyRequests`'s
          // rendering to the letter: `<day> <time>` in the pinned Dublin pair,
          // '' (so no row) on null or garbage.
          requestedAt={formatReturnAt(item.createdAt)}
          // ⭐ board 2732 M2 — the notes, as My requests shows them; the key is
          // simply ABSENT when the switch is OFF, so this screen decides nothing.
          notesSlot={item.notes}
          // ⭐ board 2732 M3 (ruling RB) — actions on the UPCOMING bucket only
          // (dated today or later in Dublin, or unreadable): exactly the set My
          // requests lists. A Past row OFFERS nothing, so this screen never
          // invites a stand-down email for a ride that already ran. ⚠️ The screen
          // enforces that, on the device clock; the cancel route itself accepts
          // any in-scope live request whatever its date, as it always has.
          actions={inShowBucket(item.journeyDate, 'upcoming', nowIso) ? actionsFor(item) : undefined}
        />
      </li>
    );
  }

  let body;
  if (error) {
    body = (
      <>
        <p class="screen-error" role="alert">
          {error}
        </p>
        <Button variant="primary" onClick={() => setRetry((n) => n + 1)}>
          {sharedCopy.tryAgain}
        </Button>
      </>
    );
  } else if (!items) {
    body = (
      <p class="loading-notice" role="status">
        {sharedCopy.loading}
      </p>
    );
  } else if (items.length === 0 && !crossGroupEnabled && group === null && staffId === '') {
    // ⭐ The gated state (plan A6, D10): the flag is OFF, so only what this
    // account entered is listed, and it has entered nothing. Neutral — and a
    // NEUTRAL HEADLINE, not the gated sentence: the persistent `Notice` below
    // already says it, and the M5 ux-review read the same sentence twice.
    body = <EmptyState headline={homeRequestsCopy.homeRequestsEmptyGated} />;
  } else {
    // ⚠️ board 2707 M4: the status filter keys the STATUS's nurse word, not the
    // pill's, so `Sent` still keeps every `new` and `priced` row (not yet
    // confirmed) though a seen one's pill now reads `Seen` (ruling RG). Whether
    // the select should offer `Seen` too is routed to the plan's M7 OPEN-QUESTIONS.
    const shown = items.filter(
      (i) => inShowBucket(i.journeyDate, show, nowIso)
        && (status === '' || myRequestsCopy.nurseStatusLabels[i.status] === status),
    );
    // By date: Upcoming soonest first (today's rows lead), Past newest first —
    // the wire is newest-first already, so Past keeps it. A row with no
    // readable day sorts LAST under Upcoming; it has no place in a soonest-first
    // order, and it is still shown.
    const byDate = show === 'upcoming'
      ? [...shown].sort((a, b) => {
        const da = dublinDayNumber(a.journeyDate) ?? Number.POSITIVE_INFINITY;
        const db = dublinDayNumber(b.journeyDate) ?? Number.POSITIVE_INFINITY;
        if (da !== db) return da < db ? -1 : 1;
        const ia = a.journeyDate ?? '';
        const ib = b.journeyDate ?? '';
        return ia < ib ? -1 : ia > ib ? 1 : 0;
      })
      : shown;
    // By staff (R-14): under a heading per name, most requests first, then A to Z.
    const byStaff = new Map<string, HomeRequestListItem[]>();
    for (const i of byDate) {
      const key = i.requestedBy ?? homeRequestsCopy.byStaffUnnamed;
      byStaff.set(key, [...(byStaff.get(key) ?? []), i]);
    }
    const staffGroups = [...byStaff.entries()].sort(
      ([na, a], [nb, b]) => b.length - a.length || na.localeCompare(nb, 'en-IE'),
    );

    body = (
      <>
        <div class="home-requests-filters">
          <div class="home-requests-filter" role="group" aria-label={homeRequestsCopy.showLegend}>
            <span class="home-requests-filter-word" aria-hidden="true">{homeRequestsCopy.showLegend}</span>
            <Chip shape="filter" pressed={show === 'upcoming'} onClick={() => setShow('upcoming')}>
              {homeRequestsCopy.showUpcoming}
            </Chip>
            <Chip shape="filter" pressed={show === 'past'} onClick={() => setShow('past')}>
              {homeRequestsCopy.showPast}
            </Chip>
          </div>
          {groups.length > 0 && (
            <div class="home-requests-filter" role="group" aria-label={homeRequestsCopy.groupLegend}>
              <span class="home-requests-filter-word" aria-hidden="true">{homeRequestsCopy.groupLegend}</span>
              {/* ⭐ T2075 M5 (closing the M4 ux-review) — a leading `All` chip,
                  pressed when no group is chosen: a one-tap clear beside the
                  un-press the chosen chip already offered. */}
              <Chip shape="filter" pressed={group === null} onClick={() => chooseGroup(null)}>
                {homeRequestsCopy.homeRequestsAllGroups}
              </Chip>
              {groups.map((g) => (
                <Chip key={g} shape="filter" pressed={group === g} onClick={() => chooseGroup(g)}>
                  {g}
                </Chip>
              ))}
            </div>
          )}
          <div class="home-requests-filter" role="group" aria-label={homeRequestsCopy.viewLegend}>
            <span class="home-requests-filter-word" aria-hidden="true">{homeRequestsCopy.viewLegend}</span>
            <Chip shape="filter" pressed={view === 'date'} onClick={() => setView('date')}>
              {homeRequestsCopy.viewByDate}
            </Chip>
            <Chip shape="filter" pressed={view === 'staff'} onClick={() => setView('staff')}>
              {homeRequestsCopy.viewByStaff}
            </Chip>
          </div>
          <FormField
            id="hr-staff"
            kind="select"
            label={homeRequestsCopy.staffLabel}
            value={staffId}
            options={staffOptions}
            onInput={setStaffId}
          />
          <FormField
            id="hr-status"
            kind="select"
            label={homeRequestsCopy.statusLabel}
            value={status}
            options={statusOptions}
            onInput={setStatus}
          />
        </div>
        {/* What the filters DID, announced politely: one status line, always
            present, so a filter that empties the list is heard and not only seen. */}
        <p class="visually-hidden" role="status">
          {homeRequestsCopy.shown(shown.length)}
        </p>
        {shown.length === 0 ? (
          <EmptyState headline={homeRequestsCopy.homeRequestsEmpty} />
        ) : view === 'date' ? (
          <ul class="my-requests-list">{byDate.map(row)}</ul>
        ) : (
          staffGroups.map(([name, rows]) => (
            <section key={name}>
              <h2 class="my-requests-group">{homeRequestsCopy.byStaffHeading(name, rows.length)}</h2>
              <ul class="my-requests-list">{rows.map(row)}</ul>
            </section>
          ))
        )}
      </>
    );
  }

  return (
    <div class="screen home-requests-screen">
      <h1>{homeRequestsCopy.title}</h1>
      <p class="field-hint">{homeRequestsCopy.capHint(HOME_REQUESTS_CAP)}</p>
      {/* ⭐ T2075 M5 (closing the M4 ux-review) — THE GATED SIGNAL IS
          PERSISTENT: while the R-9 flag is OFF the notice sits under the hint
          whatever the row count, because an account WITH its own rows read the
          500 hint as "this is everything". The zero-rows `EmptyState` below is
          kept, with a neutral headline: the sentence is said ONCE, here. */}
      {items !== null && !error && !crossGroupEnabled && (
        <Notice kind="info">{homeRequestsCopy.homeRequestsGatedNotice}</Notice>
      )}
      {body}
    </div>
  );
}

/** "Room 12" from a stored room value, through the roster's own rule
 *  (`myResidentsCopy.roomLine`: a value already prefixed is not doubled). */
function myResidentsRoom(room: string): string {
  return room.trim() === '' ? '' : myResidentsCopy.roomLine(room);
}
